import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Upload, FileSpreadsheet, CheckCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomersImportPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadSuccess(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error('Pilih file Excel terlebih dahulu');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/customers/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(response.data.message);
      setUploadSuccess(true);
      setFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload gagal. Periksa format file Excel Anda.');
    } finally {
      setUploading(false);
    }
  };

  // --- SUNTIKAN HEADER BARU (KOLOM N & O) ---
  const expectedHeaders = [
    'CustomerID', 'Company', 'Building', 'Address1', 'Address2', 'City',
    'PostCode', 'PhoneArea', 'Phone1', 'Phone2', 'WORLD_ACC', 'ContactPerson',
    'Rate_Code', 'Rate_Code_Imp', 'Rate_Code_Dom', 'NPWP', 'SalesOcs', 'SalesArea', 
    'DateApproval', 'Emailaddress', 'Curr', 'CityF', 'TaxName', 'TaxAddress', 'BillName'
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="import-page">
      <div>
        <h1 className="text-4xl font-heading font-bold mb-2">Import Customer Data</h1>
        <p className="text-muted-foreground text-lg">Upload file Excel untuk menambah atau meng-update data customer secara batch (Upsert).</p>
      </div>

      <Card className="border-none shadow-[0_8px_40px_rgb(0,0,0,0.08)] rounded-2xl">
        <CardHeader>
          <CardTitle className="text-2xl font-heading">Upload File Excel</CardTitle>
          <CardDescription>File harus dalam format .xlsx atau .xls dengan header yang sesuai</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div
            {...getRootProps()}
            data-testid="dropzone"
            className={`border-4 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all duration-300 ${
              isDragActive
                ? 'border-primary bg-primary/10 scale-[1.02]'
                : 'border-muted-foreground/30 bg-muted/20 hover:border-primary/50 hover:bg-primary/5'
            }`}
          >
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-4">
              {file ? (
                <>
                  <div className="w-20 h-20 rounded-2xl bg-green-100 flex items-center justify-center">
                    <FileSpreadsheet className="w-10 h-10 text-green-600" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold" data-testid="file-name">{file.name}</p>
                    <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Upload className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold">
                      {isDragActive ? 'Drop file disini...' : 'Drag & drop file Excel atau klik untuk pilih'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">Hanya file .xlsx atau .xls yang diterima</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              data-testid="upload-button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1 h-12 rounded-full font-bold shadow-hard-pop hover:shadow-hard-pop-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
            >
              {uploading ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-5 h-5 mr-2" /> Proses Data</>
              )}
            </Button>
            {file && (
              <Button
                data-testid="clear-button"
                onClick={() => setFile(null)}
                variant="outline"
                className="rounded-full font-bold border-2"
              >
                Clear
              </Button>
            )}
          </div>

          {uploadSuccess && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="success-message">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div className="flex-1">
                <p className="font-semibold text-green-900">Upload Berhasil!</p>
                <p className="text-sm text-green-700">Sistem otomatis mendeteksi data baru dan meng-update data lama.</p>
              </div>
              <Button
                onClick={() => navigate('/customers/list')}
                variant="ghost"
                className="text-green-700 hover:text-green-900 font-semibold"
                data-testid="view-customers-link"
              >
                Lihat Data
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-none shadow-[0_8px_30px_rgb(0,0,0,0.06)] rounded-2xl bg-gradient-to-br from-amber-50/50 to-orange-50/30">
        <CardHeader>
          <CardTitle className="text-xl font-heading">Format Excel yang Diperlukan</CardTitle>
          <CardDescription>Pastikan file Excel Anda memiliki urutan header berikut di baris pertama (Kolom M, N, dan O adalah Rate Code):</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {expectedHeaders.map((header, index) => {
                // Menyoroti khusus 3 kolom Rate Code
                const isRateColumn = header.includes('Rate_Code');
                return (
                    <div key={index} className={`px-3 py-2 rounded-lg text-sm font-medium border ${isRateColumn ? 'bg-amber-100 border-amber-300 text-amber-900' : 'bg-white border-border'}`}>
                        {header}
                    </div>
                )
            })}
          </div>
          <div className="mt-4 p-4 bg-white/60 rounded-xl">
            <p className="text-sm text-muted-foreground">
              <strong>Tip Upsert:</strong> Jika CustomerID sudah ada di database, sistem hanya akan meng-update datanya. Jika CustomerID belum ada, sistem akan membuat data baru.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomersImportPage;